/**
 * ListingContactSection — Phase 36 wizard Step 2 sub-section.
 *
 * Captures who's selling (Realtor / FSBO / Wholesaler / Off-market /
 * Unknown) and how to reach them. All fields are optional. The
 * brokerage field is hidden for non-realtor; the cell field hides
 * when "Cell same as phone" is checked.
 *
 * Email + phone get inline red-border validation on blur. Server
 * does the canonical strip on save, but the client previews the
 * formatted phone as the user types so they trust the input.
 */
import { Mail, Phone, Smartphone, StickyNote, User, Building, Link2, ExternalLink } from "lucide-react";
import { useState } from "react";
import { detectListingPlatform } from "@/components/property/PropertyMeta";

const LISTING_OPTIONS = [
  { value: "realtor", label: "Realtor" },
  { value: "for_sale_by_owner", label: "FSBO" },
  { value: "wholesaler", label: "Wholesaler" },
  { value: "off_market", label: "Off-market" },
  { value: "unknown", label: "Unknown" },
];

const NAME_PLACEHOLDER = {
  realtor: "Realtor's name",
  for_sale_by_owner: "Owner's name",
  wholesaler: "Wholesaler's name",
  off_market: "Contact name",
  unknown: "Contact name",
};

// US-style live formatter: keeps up to 10 digits formatted as
// "(305) 555-1234"; falls back to digits-as-typed for 11+ to allow
// international numbers without fighting the user.
function formatPhoneAsTyping(raw, country) {
  if (!raw) return "";
  const digits = String(raw).replace(/\D/g, "");
  if (country && country !== "US") return digits; // non-US: don't auto-format
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  return `+${digits}`;
}

function isValidEmail(s) {
  if (!s) return true; // empty is fine
  return /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(s);
}

function isValidPhone(s) {
  if (!s) return true;
  const digits = String(s).replace(/\D/g, "");
  return digits.length >= 7;
}

function isValidUrl(s) {
  if (!s) return true; // empty allowed
  const t = String(s).trim();
  if (!/^https?:\/\//i.test(t)) return false;
  try {
    const u = new URL(t);
    return !!u.hostname;
  } catch {
    return false;
  }
}

export default function ListingContactSection({ wizard }) {
  const c = wizard.form.contact || {};
  const country = wizard.form.property?.country || "US";
  const [emailTouched, setEmailTouched] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [cellTouched, setCellTouched] = useState(false);
  const [urlTouched, setUrlTouched] = useState(false);

  const isRealtor = c.listing_type === "realtor";
  const emailError = emailTouched && c.contact_email && !isValidEmail(c.contact_email);
  const phoneError = phoneTouched && c.contact_phone && !isValidPhone(c.contact_phone);
  const cellError = cellTouched && c.contact_cell && !isValidPhone(c.contact_cell);
  const urlError = urlTouched && c.listing_url && !isValidUrl(c.listing_url);
  // Phase 36 polish — detect platform once URL is valid, drive the
  // "Detected: Zillow" feedback chip + colour coding.
  const detectedPlatform = c.listing_url && isValidUrl(c.listing_url)
    ? detectListingPlatform(c.listing_url) : null;

  return (
    <div className="mt-8 border-t border-slate-200 pt-6" data-testid="wizard-listing-contact">
      <div className="mb-4">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          Listing & contact (optional)
        </p>
        <p className="mt-1 text-xs text-slate-500">
          Who's selling and how to reach them. Used by Negotiation prompts and the deal page.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-6">
        {/* Listing type — segmented control */}
        <div className="sm:col-span-6">
          <label className="text-sm font-medium text-slate-700">Listing type</label>
          <div
            role="radiogroup"
            aria-label="Listing type"
            className="mt-2 inline-flex flex-wrap gap-1 rounded-xl border border-slate-200 bg-slate-50 p-1"
            data-testid="listing-type-group"
          >
            {LISTING_OPTIONS.map((opt) => {
              const active = c.listing_type === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  data-testid={`listing-type-${opt.value}`}
                  onClick={() => wizard.setContact("listing_type", opt.value)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                    active
                      ? "bg-white text-indigo-700 shadow-sm ring-1 ring-indigo-200"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Name */}
        <div className="sm:col-span-3">
          <label htmlFor="contact-name" className="text-sm font-medium text-slate-700">
            Contact name
          </label>
          <div className="mt-1 flex items-center gap-2 rounded-md border border-slate-200 px-3 shadow-sm focus-within:border-indigo-400 focus-within:ring-1 focus-within:ring-indigo-400">
            <User className="h-4 w-4 text-slate-400" aria-hidden="true" />
            <input
              id="contact-name"
              data-testid="contact-name-input"
              value={c.contact_name || ""}
              onChange={(e) => wizard.setContact("contact_name", e.target.value)}
              maxLength={200}
              placeholder={NAME_PLACEHOLDER[c.listing_type] || "Contact name"}
              className="block w-full border-0 bg-transparent py-2 text-sm focus:outline-none focus:ring-0"
            />
          </div>
        </div>

        {/* Brokerage — realtor only */}
        {isRealtor ? (
          <div className="sm:col-span-3" data-testid="contact-brokerage-field">
            <label htmlFor="contact-brokerage" className="text-sm font-medium text-slate-700">
              Brokerage
            </label>
            <div className="mt-1 flex items-center gap-2 rounded-md border border-slate-200 px-3 shadow-sm focus-within:border-indigo-400 focus-within:ring-1 focus-within:ring-indigo-400">
              <Building className="h-4 w-4 text-slate-400" aria-hidden="true" />
              <input
                id="contact-brokerage"
                data-testid="contact-brokerage-input"
                value={c.contact_brokerage || ""}
                onChange={(e) => wizard.setContact("contact_brokerage", e.target.value)}
                maxLength={200}
                placeholder="e.g. Coldwell Banker"
                className="block w-full border-0 bg-transparent py-2 text-sm focus:outline-none focus:ring-0"
              />
            </div>
          </div>
        ) : null}

        {/* Email */}
        <div className="sm:col-span-3">
          <label htmlFor="contact-email" className="text-sm font-medium text-slate-700">
            Email
          </label>
          <div className={`mt-1 flex items-center gap-2 rounded-md border px-3 shadow-sm focus-within:ring-1 ${
            emailError ? "border-rose-400 focus-within:border-rose-500 focus-within:ring-rose-400"
                       : "border-slate-200 focus-within:border-indigo-400 focus-within:ring-indigo-400"
          }`}>
            <Mail className="h-4 w-4 text-slate-400" aria-hidden="true" />
            <input
              id="contact-email"
              type="email"
              data-testid="contact-email-input"
              value={c.contact_email || ""}
              onChange={(e) => wizard.setContact("contact_email", e.target.value)}
              onBlur={() => setEmailTouched(true)}
              maxLength={200}
              placeholder="maria@firm.com"
              className="block w-full border-0 bg-transparent py-2 text-sm focus:outline-none focus:ring-0"
            />
          </div>
          {emailError ? (
            <p className="mt-1 text-xs text-rose-600" data-testid="contact-email-error">
              Enter a valid email or leave blank.
            </p>
          ) : null}
        </div>

        {/* Phone */}
        <div className="sm:col-span-3">
          <label htmlFor="contact-phone" className="text-sm font-medium text-slate-700">
            Phone
          </label>
          <div className={`mt-1 flex items-center gap-2 rounded-md border px-3 shadow-sm focus-within:ring-1 ${
            phoneError ? "border-rose-400 focus-within:border-rose-500 focus-within:ring-rose-400"
                       : "border-slate-200 focus-within:border-indigo-400 focus-within:ring-indigo-400"
          }`}>
            <Phone className="h-4 w-4 text-slate-400" aria-hidden="true" />
            <input
              id="contact-phone"
              type="tel"
              data-testid="contact-phone-input"
              value={c.contact_phone || ""}
              onChange={(e) => wizard.setContact("contact_phone", formatPhoneAsTyping(e.target.value, country))}
              onBlur={() => setPhoneTouched(true)}
              maxLength={40}
              placeholder={country === "US" ? "(305) 555-1234" : "+44 20 7946 0958"}
              className="block w-full border-0 bg-transparent py-2 text-sm focus:outline-none focus:ring-0"
            />
          </div>
          {phoneError ? (
            <p className="mt-1 text-xs text-rose-600" data-testid="contact-phone-error">
              Phone must have at least 7 digits.
            </p>
          ) : null}
        </div>

        {/* Cell same as phone */}
        <div className="sm:col-span-6">
          <label className="inline-flex items-center gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              data-testid="contact-same-as-phone"
              checked={!!c.contact_phone_same_as_cell}
              onChange={(e) => wizard.setContact("contact_phone_same_as_cell", e.target.checked)}
              className="h-3.5 w-3.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
            />
            Cell is the same as phone
          </label>
        </div>

        {/* Cell — hidden when same as phone */}
        {!c.contact_phone_same_as_cell ? (
          <div className="sm:col-span-3" data-testid="contact-cell-field">
            <label htmlFor="contact-cell" className="text-sm font-medium text-slate-700">
              Cell
            </label>
            <div className={`mt-1 flex items-center gap-2 rounded-md border px-3 shadow-sm focus-within:ring-1 ${
              cellError ? "border-rose-400 focus-within:border-rose-500 focus-within:ring-rose-400"
                         : "border-slate-200 focus-within:border-indigo-400 focus-within:ring-indigo-400"
            }`}>
              <Smartphone className="h-4 w-4 text-slate-400" aria-hidden="true" />
              <input
                id="contact-cell"
                type="tel"
                data-testid="contact-cell-input"
                value={c.contact_cell || ""}
                onChange={(e) => wizard.setContact("contact_cell", formatPhoneAsTyping(e.target.value, country))}
                onBlur={() => setCellTouched(true)}
                maxLength={40}
                placeholder={country === "US" ? "(305) 555-9999" : "+44 7700 900123"}
                className="block w-full border-0 bg-transparent py-2 text-sm focus:outline-none focus:ring-0"
              />
            </div>
            {cellError ? (
              <p className="mt-1 text-xs text-rose-600" data-testid="contact-cell-error">
                Cell must have at least 7 digits.
              </p>
            ) : null}
          </div>
        ) : null}

        {/* Listing URL — full width above Notes */}
        <div className="sm:col-span-6" data-testid="contact-listing-url-field">
          <div className="flex items-center justify-between">
            <label htmlFor="contact-listing-url" className="text-sm font-medium text-slate-700">
              Listing URL
            </label>
            {detectedPlatform ? (
              <span
                data-testid="contact-detected-platform"
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ${detectedPlatform.badgeClass}`}
              >
                Detected: {detectedPlatform.label}
              </span>
            ) : null}
          </div>
          <div className={`mt-1 flex items-center gap-2 rounded-md border px-3 shadow-sm focus-within:ring-1 ${
            urlError ? "border-rose-400 focus-within:border-rose-500 focus-within:ring-rose-400"
                     : "border-slate-200 focus-within:border-indigo-400 focus-within:ring-indigo-400"
          }`}>
            <Link2 className="h-4 w-4 text-slate-400" aria-hidden="true" />
            <input
              id="contact-listing-url"
              type="url"
              data-testid="contact-listing-url-input"
              value={c.listing_url || ""}
              onChange={(e) => wizard.setContact("listing_url", e.target.value)}
              onBlur={() => setUrlTouched(true)}
              maxLength={2000}
              placeholder="https://zillow.com/homedetails/..."
              className="block w-full border-0 bg-transparent py-2 text-sm focus:outline-none focus:ring-0"
            />
            {c.listing_url && isValidUrl(c.listing_url) ? (
              <a
                href={c.listing_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0 text-slate-400 hover:text-indigo-600"
                title="Open listing in new tab"
                data-testid="contact-listing-url-preview"
              >
                <ExternalLink className="h-4 w-4" aria-hidden="true" />
              </a>
            ) : null}
          </div>
          {urlError ? (
            <p className="mt-1 text-xs text-rose-600" data-testid="contact-listing-url-error">
              URL must start with http:// or https:// and include a hostname.
            </p>
          ) : null}
        </div>

        {/* Notes */}
        <div className="sm:col-span-6">
          <label htmlFor="contact-notes" className="text-sm font-medium text-slate-700">
            Notes (optional)
          </label>
          <div className="mt-1 flex items-start gap-2 rounded-md border border-slate-200 px-3 py-2 shadow-sm focus-within:border-indigo-400 focus-within:ring-1 focus-within:ring-indigo-400">
            <StickyNote className="mt-1 h-4 w-4 flex-shrink-0 text-slate-400" aria-hidden="true" />
            <textarea
              id="contact-notes"
              data-testid="contact-notes-input"
              rows={2}
              value={c.contact_notes || ""}
              onChange={(e) => wizard.setContact("contact_notes", e.target.value.slice(0, 500))}
              maxLength={500}
              placeholder="e.g. Speaks Spanish, best AM"
              className="block w-full border-0 bg-transparent text-sm focus:outline-none focus:ring-0"
            />
          </div>
          <p className="mt-1 text-[10px] text-slate-400">
            {(c.contact_notes || "").length} / 500
          </p>
        </div>
      </div>
    </div>
  );
}
